class CollectionPinnedGrid extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.pages = new Map();
    this.active = this.dataset.active === 'true';
    this.pageSize = Number(this.dataset.pageSize);
    this.currentPage = Number(this.dataset.page) || 1;
    queueMicrotask(() => {
      if (!this.isConnected || !this.active) return;
      this.container = this.closest('#ProductGridContainer');
      this.grid = this.container?.querySelector('#product-grid');
      if (!this.grid || !this.pageSize) { this.active = false; return; }
      // On a subcategory tab the URL still names the parent; build from the tab's collection
      // and the params facets.js fetched it with instead
      const tabUrl = document.querySelector('collection-tabs')?.activeUrl;
      this.baseUrl = tabUrl
        ? new URL(`${tabUrl}?${FacetFiltersForm.searchParamsPrev || ''}`, window.location.origin)
        : new URL(window.location.href);
      this.baseUrl.searchParams.delete('section_id');
      this.baseUrl.searchParams.delete('sections');
      this.baseUrl.searchParams.set('sort_by', 'best-selling');
      this.pages.set(this.currentPage, Promise.resolve(this.readCards(this.grid)));
      this.grid.setAttribute('aria-busy', 'true');
      this.ready = this.initialise().catch((error) => {
        this.active = false;
        if (error.name !== 'AbortError') console.error('Collection ordering:', error);
      }).finally(() => {
  this.grid?.removeAttribute('aria-busy');
  this.grid?.removeAttribute('data-pins-loading');
});
    });
  }

  disconnectedCallback() { this.abort?.abort(); }

  readCards(grid) {
    return new Map(Array.from(grid.querySelectorAll(':scope > li[data-product-id]'),
      (item) => [item.dataset.productId, item.cloneNode(true)]));
  }

  async fetchHtml(url) {
    const response = await fetch(url, { signal: this.abort.signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  async initialise() {
   
    const ids = [];
    const availableIds = new Set();
    let pinIds = [];
    let total = 0;
    let page = 1;
    let lastPage = 1;

    do {
      const url = new URL(this.baseUrl);
      url.searchParams.set('section_id', 'collection-pin-data');
      url.searchParams.set('page', String(page));
      const parsed = await this.fetchHtml(url);
      const dataElement = parsed.querySelector('[data-collection-pin-data]');
      if (!dataElement) throw new Error('Collection pin data section is missing.');
      const data = JSON.parse(dataElement.textContent);
      if (!Array.isArray(data.product_ids)) throw new Error('Invalid collection data.');
      if (String(data.collection_id) !== this.dataset.collectionId) throw new Error('Collection mismatch.');
      if (Number(data.page) !== page) throw new Error('Collection data pagination mismatch.');
      if (page === 1) {
        pinIds = (data.pinned_ids || []).map(String);
        total = Number(data.total);
        lastPage = Number(data.pages) || 1;
      }
      
      if (!Array.isArray(data.available_ids)) {
        throw new Error('Collection availability data is missing.');
      }
      ids.push(...data.product_ids.map(String));
      data.available_ids.forEach((id) => availableIds.add(String(id)));
      page += 1;

    } while (page <= lastPage);
    if (new Set(ids).size !== ids.length || ids.length !== total) throw new Error('Incomplete collection index.');
    const nativePositions = new Map(ids.map((id, index) => [id, index]));
   
    const matchingPins = [...new Set(pinIds)].filter((id) =>
      nativePositions.has(id) && availableIds.has(id)
    );
    const pinSet = new Set(matchingPins);

    const availableProducts = ids.filter((id) =>
      availableIds.has(id) && !pinSet.has(id)
    );

    const unavailableProducts = ids.filter((id) =>
      !availableIds.has(id)
    );

    this.order = [
      ...matchingPins,
      ...availableProducts,
      ...unavailableProducts
    ];

    this.nativePositions = nativePositions;
    const cards = await this.buildPage(this.currentPage);
    if (!this.isConnected) return;
    this.keepStyles();
    this.grid.replaceChildren(...cards);
    this.updateNextButton();
  }

  keepStyles() {
    this.grid.querySelectorAll('link[rel="stylesheet"], style').forEach((style) => {
      const copy = style.cloneNode(true);
      this.container.appendChild(copy);
    });
  }

  async sourcePage(page) {
    if (!this.pages.has(page)) {
      const url = new URL(this.baseUrl);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('page', String(page));
      this.pages.set(page, this.fetchHtml(url).then((parsed) => {
        const grid = parsed.querySelector('#product-grid');
        if (!grid) throw new Error('Product grid is missing.');
        return this.readCards(grid);
      }));
    }
    return this.pages.get(page);
  }

  async buildPage(page) {
    const start = (page - 1) * this.pageSize;
    const wanted = this.order.slice(start, start + this.pageSize);
    const sourcePages = [...new Set(wanted.map((id) =>
      Math.floor(this.nativePositions.get(id) / this.pageSize) + 1))];
    const sources = new Map();
    for (let offset = 0; offset < sourcePages.length; offset += 4) {
      await Promise.all(sourcePages.slice(offset, offset + 4).map(async (number) => {
        sources.set(number, await this.sourcePage(number));
      }));
    }
    return wanted.map((id, index) => {
      const sourcePage = Math.floor(this.nativePositions.get(id) / this.pageSize) + 1;
      const source = sources.get(sourcePage)?.get(id);
      if (!source) throw new Error('A product card is missing; refresh to reload the collection.');
      const card = source.cloneNode(true);
      card.classList.add('scroll-trigger--cancel');
      card.style.setProperty('--animation-order', String(index + 1));
      this.setRank(card, start + index + 1);
      if (page === 1 && index < 4) card.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; });
      return card;
    });
  }

  setRank(card, number) {
    const show = this.dataset.rankEnabled === 'true' && number <= Number(this.dataset.rankMax);
    let badge = card.querySelector('.mrs-card__rank');
    if (show) {
      if (!badge) {
        const content = card.querySelector('.card__inner > .card__content');
        if (!content) throw new Error('Card rank container is missing.');
        badge = document.createElement('span');
        badge.className = 'mrs-card__rank';
        badge.setAttribute('aria-hidden', 'true');
        content.prepend(badge);
      }
      badge.textContent = `#${number}`;
    } else {
      badge?.remove();
    }
    card.querySelectorAll('.mrs-card__flag').forEach((flag) =>
      flag.classList.toggle('mrs-card__flag--right', show));
  }

  updateNextButton() {
    const button = this.container.querySelector('[data-load-more-button]');
    if (!button) return;
    if (this.currentPage * this.pageSize >= this.order.length) {
      button.remove();
      return;
    }
    const url = new URL(this.baseUrl);
    url.searchParams.set('page', String(this.currentPage + 1));
    button.setAttribute('href', url.pathname + url.search);
  }

  async loadNext() {
    await this.ready;
    if (!this.active || !this.isConnected || this.loading) return;
    this.loading = true;
    const control = this.container.querySelector('load-more-products');
    control?.setAttribute('aria-busy', 'true');
    try {
      const nextPage = this.currentPage + 1;
      const cards = await this.buildPage(nextPage);
      if (!this.isConnected) return;
      const existing = new Set(Array.from(this.grid.children, (card) => card.dataset.productId));
      if (cards.some((card) => existing.has(card.dataset.productId))) throw new Error('Duplicate product detected.');
      this.grid.append(...cards);
      this.currentPage = nextPage;
      this.updateNextButton();
      const status = control?.querySelector('.cl-load-more__status');
      if (status) status.textContent = String(this.grid.children.length);
      cards[0]?.querySelector('a, button')?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Collection load more:', error);
        const link = this.container.querySelector('[data-load-more-button]');
        if (link) window.location.href = link.href;
      }
    } finally {
      this.loading = false;
      control?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('collection-pinned-grid')) {
  customElements.define('collection-pinned-grid', CollectionPinnedGrid);
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-load-more-button]');
    const controller = button?.closest('#ProductGridContainer')?.querySelector('collection-pinned-grid');
    if (!controller?.active) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    Promise.resolve(controller.ready).then(() => {
      if (controller.active && controller.isConnected) controller.loadNext();
      else if (button.isConnected) window.location.href = button.href;
    });
  }, true);
}
